import { useEffect, useId, useRef, useState } from 'react';
import Button from '../inputs/Button';

// One dialog for every "are you sure?" moment in the audience builder.
//
// Was UnchangedAudienceNameModal, which only ever warned about the default
// audience name. ATAD-134 added two more checks — wide-open filters at Get
// counts, and an oversized audience at Continue Order — so the copy moved out
// to the call sites and this became a shell that renders a list of warnings.
//
// Two modes:
//   BLOCKING  (no onProceed) — one button, no way past. The naming warning.
//   ADVISORY  (with onProceed) — "Review my filters" / "Continue anyway".
//
// `onCancel` is always the SAFE action, and is the only required handler. That
// is what lets Escape, the backdrop, and the blocking button all bind to one
// handler with no branching — and it guarantees that dismissing the dialog by
// any incidental route can never mean "Continue anyway".

export type AudienceWarning = {
  id: string;
  /** Bold lead-in. Rendered only in list layout. */
  title?: string;
  /** ReactNode so callers can bold a number or an attribute name inline. */
  body: React.ReactNode;
};

export type AudienceWarningModalProps = {
  show: boolean;
  heading?: string;
  warnings: AudienceWarning[];
  /** 'prose' renders bodies bare and centered; 'list' renders a bulleted list. */
  layout?: 'prose' | 'list';
  blocking?: boolean;
  /** The safe action. Also bound to Escape and the backdrop. */
  onCancel: () => void;
  cancelLabel?: string;
  /** Advisory only. Omit for a blocking dialog. */
  onProceed?: () => void;
  proceedLabel?: string;
  /** Optional lead-in above the list. */
  intro?: React.ReactNode;
  /** Optional small print below the list. */
  footnote?: React.ReactNode;
};

// A user who drags in six likelihood scores gets six warnings. Cap the visible
// list so the dialog can't outgrow the viewport.
const MAX_VISIBLE_WARNINGS = 6;

/** Preserved verbatim from UnchangedAudienceNameModal. */
export const UNCHANGED_NAME_WARNING: AudienceWarning[] = [
  {
    id: 'unchanged-name',
    body: (
      <>
        <p className="text-gray-600">
          You are using the default audience name. Please provide a descriptive
          name for your audience before continuing with your order.
        </p>
        <p className="text-sm text-gray-500">
          Naming your audience helps you identify and manage your saved
          audiences more easily in the future.
        </p>
      </>
    ),
  },
];

export default function AudienceWarningModal({
  show,
  heading = 'Before you continue',
  warnings,
  layout = 'list',
  blocking = false,
  onCancel,
  cancelLabel = 'Review my filters',
  onProceed,
  proceedLabel = 'Continue anyway',
  intro,
  footnote,
}: AudienceWarningModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const headingId = useId();
  // Button is a plain function component (no forwardRef), so focus is taken
  // via the footer container rather than by modifying that shared component.
  const footerRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocusedRef = useRef<Element | null>(null);

  useEffect(() => {
    if (show) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [show]);

  // Escape dismisses to the SAFE action, never to "Continue anyway".
  useEffect(() => {
    if (!show) return undefined;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [show, onCancel]);

  // Focus the safe button on open, restore on close. Without this the dialog
  // has no keyboard entry point and Tab lands somewhere behind the overlay.
  useEffect(() => {
    if (show) {
      previouslyFocusedRef.current = document.activeElement;
      footerRef.current?.querySelector('button')?.focus();
      return undefined;
    }
    const previous = previouslyFocusedRef.current as HTMLElement | null;
    previous?.focus?.();
    previouslyFocusedRef.current = null;
    return undefined;
  }, [show]);

  if (!show && !isOpen) return null;

  const visible = warnings.slice(0, MAX_VISIBLE_WARNINGS);
  const overflow = warnings.length - visible.length;

  return (
    <div
      className={`fixed max-w-full max-h-full inset-0 z-50 flex justify-center items-center transition-all duration-300 ease-in-out ${
        isOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-125'
      }`}
    >
      {/* Overlay first so the panel stacks above it without relying on
          source-order luck, the way the old absolute/h-screen version did. */}
      {show && (
        <div
          className="absolute inset-0 bg-gray-500 opacity-50 cursor-default"
          onClick={onCancel}
        />
      )}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        className="relative z-50 w-3/4 lg:w-1/2 xl:w-2/5 bg-white rounded-half p-5 min-w-min min-h-min shadow-2xl"
      >
        <div className="h-full w-full flex flex-col">
          <div className="flex flex-col flex-grow pt-2">
            <div className="flex flex-grow-0 justify-center">
              <h4 id={headingId} className="text-xl font-bold">
                {heading}
              </h4>
            </div>

            {layout === 'prose' ? (
              <div className="flex flex-col flex-grow justify-center items-center w-full pb-10 pt-4 space-y-4 text-center px-4">
                {visible.map((warning) => (
                  <div key={warning.id} className="space-y-4">
                    {warning.body}
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col flex-grow w-full pb-8 pt-4 space-y-4 px-4">
                {intro && <p className="text-gray-600">{intro}</p>}
                <ul className="space-y-2 list-disc pl-5">
                  {visible.map((warning) => (
                    <li key={warning.id} className="text-gray-600">
                      {warning.title && (
                        <span className="font-bold">{warning.title}</span>
                      )}
                      {warning.title && warning.body ? ' — ' : null}
                      {warning.body}
                    </li>
                  ))}
                </ul>
                {overflow > 0 && (
                  <p className="text-sm text-gray-500 pl-5">
                    …and {overflow} more
                  </p>
                )}
                {footnote && (
                  <p className="text-sm text-gray-500">{footnote}</p>
                )}
              </div>
            )}
          </div>

          <div
            ref={footerRef}
            className="flex flex-grow-0 justify-center gap-4 pb-8"
          >
            {/* Safe action first, so it is what focus and Tab reach first. */}
            <Button type="button" primary onClick={onCancel}>
              {cancelLabel}
            </Button>
            {!blocking && onProceed && (
              <Button type="button" secondary onClick={onProceed}>
                {proceedLabel}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
